import { DatePipe, DecimalPipe } from '@angular/common';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FlexModule } from '@angular/flex-layout';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatError, MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput, MatInputModule } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatRow, MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { saveAs } from 'file-saver';
import { CurrencyMaskModule } from 'ng2-currency-mask';
import { finalize } from 'rxjs';
import { BreadcrumbsComponent } from '../../../shared/components/breadcrumbs/breadcrumbs.component';
import { CustomHeader } from '../../../shared/components/custom-datepicker-header/custom-datepicker-header.component';
import { AlertComponent } from '../../../shared/components/dialogs/alert/alert.component';
import { ConfirmDialogComponent } from '../../../shared/components/dialogs/confirm/confirm.component';
import { LoaderDialogComponent } from '../../../shared/components/loader-dialog/loader-dialog.component';
import { BreadcrumbsOptions } from '../../../shared/models/breadcrumbs-model';
import { OrderItemDTO } from '../../models/order';
import { BudgetsService } from '../../services/budgets/budgets.service';
import { OrdersService } from '../../services/orders/orders.service';

@Component({
  selector: 'app-budget-inclusion',
  templateUrl: './budget-inclusion.component.html',
  styleUrls: ['./budget-inclusion.component.scss'],
  standalone: true,
  imports: [
    FlexModule,
    ReactiveFormsModule,
    MatFormField,
    MatInputModule,
    MatInput,
    MatLabel,
    BreadcrumbsComponent,
    MatButton,
    TranslatePipe,
    MatTableModule,
    MatRow,
    MatIcon,
    MatIconButton,
    MatSelect,
    MatOption,
    MatTooltip,
    DecimalPipe,
    MatDatepickerModule,
    MatNativeDateModule,
    MatSuffix,
    MatError,
    CurrencyMaskModule,
    DatePipe,
  ],
  providers: [DatePipe]
})

export class BudgetInclusionComponent implements OnInit {
  customHeader = CustomHeader;

  breadcrumbsData: BreadcrumbsOptions = {
    title: 'budget-inclusion.title',
    buttons: [
      { label: 'button.cancel', style: 'red', do: () => this.cancel() },
      { label: 'button.save', style: 'green', disabled: true, do: () => this.save() }
    ]
  };

  file: any;
  fileName: string = '';
  isEdit: boolean = false;
  editingCost: any = null;
  totalOrderValue: number = 0;
  budgetForm: FormGroup;
  additionalInfoForm: FormGroup;
  additionalCostsForm: FormGroup;
  fileForm: FormGroup;
  orderItemForms: FormGroup[] = [];

  isReadOnly = false;
  isNotWaitingBudget = false;
  filesTableHasAtLeastAnItem = false;

  orderId!: number;
  budgetId!: number;
  projectType = '';
  projectDescription = '';
  costTypeOptions: any[] = [];
  orderItems: OrderItemDTO[] = [];
  dataSource = new MatTableDataSource<any>();
  filedataSource = new MatTableDataSource<any>();
  displayedColumns = ['costType', 'description', 'value', 'action'];
  displayedColumnsFiles = ['uploadDate', 'description', 'file', 'action'];

  @ViewChild('fileUpload') fileInput?: ElementRef;

  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private ordersService: OrdersService,
    private budgetsService: BudgetsService,
    private router: Router,
    private dialog: MatDialog
  ) {
    this.budgetForm = this.formBuilder.group({
      supplier: [''],
      file: ['', Validators.required]
    });

    this.additionalInfoForm = this.formBuilder.group({
      budgetResponseDate: ['', Validators.required],
      paymentTermDays: ['', Validators.required],
      deliveryTermDays: ['', Validators.required],
      budgetObservations: ['']
    });

    this.additionalCostsForm = this.formBuilder.group({
      costType: [null],
      description: [''],
      value: ['']
    });

    this.fileForm = this.formBuilder.group({
      description: ['', Validators.required],
      fileName: ['', Validators.required],
      document: [''],
      uploadDate: ['']
    });
  }

  ngOnInit() {
    this.initializeComponent();
  }

  private initializeComponent(): void {
    this.budgetId = +this.route.snapshot.paramMap.get('id')!;

    this.route.queryParams.subscribe(queryParams => {
      this.orderId = +queryParams['orderId'];
      this.projectType = queryParams['projectType'];
      this.projectDescription = queryParams['projectDescription'];
      this.budgetForm.patchValue({ supplier: queryParams['supplierCorporateName'] });
    });

    this.budgetsService.getAllCostTypes().subscribe({
      next: (types: any) => this.costTypeOptions = types as any[],
      error: () => this.showLoadError()
    });

    this.budgetsService.getBudgetDetails(this.budgetId).subscribe({
      next: (dto: any) => {
        this.isReadOnly = dto.readOnly;
        this.isNotWaitingBudget = dto.isNotWaitingBudget;

        if (this.isNotWaitingBudget) {
          this.patchForms(dto);
          this.filedataSource.data = dto.files;
          this.filesTableHasAtLeastAnItem = dto.files?.length > 0;
        } else {
          this.ordersService.getOrderItems(this.orderId).subscribe({
            next: response => {
              this.orderItems = (response.items || []).map(detail => ({
                id: detail.id,
                item: detail.item,
                orderItemId: detail.orderItemId,
                requestedQuantity: detail.requestedQuantity,
                availableQuantity: detail.availableQuantity,
                unitValue: detail.unitValue,
                kit: detail.kit
              }));
              this.initializeOrderItemForms();
            },
            error: () => this.showLoadError()
          });
        }

        this.configureMode();
        this.checkFormsValidity();
      },
      error: () => this.showLoadError()
    });
  }

  private showLoadError(): void {
    this.dialog.open(AlertComponent, {
      disableClose: true,
      data: {
        title: 'alert.information',
        message: 'error.loading-data'
      }
    });
  }

  private configureMode(): void {
    if (this.isReadOnly) {
      this.additionalCostsForm.disable();
      this.orderItemForms.forEach(f => f.disable());
      this.displayedColumns = this.displayedColumns.filter(c => c !== 'action');
      this.updateBreadcrumbButton();
      this.removeRequiredValidators();
    } else {
      this.additionalInfoForm.valueChanges.subscribe(() => this.checkFormsValidity());
      this.budgetForm.valueChanges.subscribe(() => this.checkFormsValidity());
    }
  }

  private updateBreadcrumbButton(): void {
    this.breadcrumbsData.title = 'Detalhes do Orçamento';
    this.breadcrumbsData.buttons![0].hide = false;
    this.breadcrumbsData.buttons![0].style = 'blue';
    this.breadcrumbsData.buttons![0].label = 'button.back';
    this.breadcrumbsData.buttons![0].do = () => this.cancel();
    this.breadcrumbsData.buttons![1].hide = true;
  }

  private removeRequiredValidators(): void {
    [
      this.additionalInfoForm.get('budgetResponseDate'),
      this.additionalInfoForm.get('paymentTermDays'),
      this.additionalInfoForm.get('deliveryTermDays'),
      this.additionalInfoForm.get('budgetObservations'),
      this.additionalCostsForm.get('costType'),
      this.additionalCostsForm.get('value')
    ].forEach(ctrl => {
      if (ctrl) {
        ctrl.clearValidators();
        ctrl.updateValueAndValidity({ emitEvent: false });
      }
    });

    this.orderItemForms.forEach(form => {
      ['availableQuantity', 'unitValue'].forEach(key => {
        const c = form.get(key);
        if (c) {
          c.clearValidators();
          c.updateValueAndValidity({ emitEvent: false });
        }
      });
    });
  }

  patchForms(dto: any): void {
    this.budgetForm.patchValue({ file: dto.fileNames });
    this.dataSource.data = dto.additionalCosts || [];
    this.orderItems = dto.budgetItems.map((b: any) => ({
      id: b.id,
      item: {
        id: b.itemId,
        partNumber: b.partNumber,
        description: b.orderItemName,
        manufacturer: b.manufacturer
      },
      orderItemId: b.orderItemId,
      requestedQuantity: b.requestedQuantity,
      availableQuantity: b.availableQuantity,
      unitValue: b.unitValue
    }));

    const date = new Date(dto.budgetResponseDate);
    this.additionalInfoForm.patchValue({
      budgetResponseDate: new Date(date.getTime() + date.getTimezoneOffset() * 60000),
      paymentTermDays: dto.paymentTermDays,
      deliveryTermDays: dto.deliveryTermDays,
      budgetObservations: dto.budgetObservations
    });

    this.initializeOrderItemForms();
  }

  initializeOrderItemForms(): void {
    this.orderItemForms = [];

    this.orderItems.forEach(item => {
      const fg = this.formBuilder.group({
        requestedQuantity: [{ value: item.requestedQuantity, disabled: true }],
        availableQuantity: [
          item.availableQuantity ?? 0,
          this.isReadOnly
            ? []
            : [
                Validators.required,
                Validators.pattern(/^[0-9]+$/),
                Validators.min(0),
                Validators.max(item.requestedQuantity ?? 0),
              ]
        ],
        unitValue: [
          item.unitValue ?? 0,
          this.isReadOnly ? [] : [Validators.required]
        ],
        totalValue: [{
          value: (item.availableQuantity || 0) * (item.unitValue || 0),
          disabled: true
        }]
      });

      fg.valueChanges.subscribe(vals => {
        const total = (vals.availableQuantity || 0) * (vals.unitValue || 0);
        fg.patchValue({ totalValue: total }, { emitEvent: false });
        this.checkFormsValidity();
        this.calculateTotalOrderValue();
      });

      this.orderItemForms.push(fg);
    });

    this.calculateTotalOrderValue();
  }


  checkFormsValidity(): void {
    console.log({
    allItemsValid: this.orderItemForms.every(f => f.valid),
    infoValid:      this.additionalInfoForm.valid,
    filesOk:        this.filesTableHasAtLeastAnItem
  });
    const allItemsValid = this.orderItemForms.every(f => f.valid);
    const infoValid = this.additionalInfoForm.valid;
    const filesOk = this.filesTableHasAtLeastAnItem;

    this.breadcrumbsData.buttons![1].disabled =
      !(allItemsValid && infoValid && filesOk);
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      const alreadyExists = this.filedataSource.data.some(existingFile => existingFile.fileName === file.name);

      if (alreadyExists) {
        this.dialog.open(AlertComponent, {
          disableClose: true,
          data: {
            message: 'budget-inclusion.file-register-duplicate-error',
            title: 'alert.information',
          },
        });
        this.fileForm.reset();
        this.fileInput!.nativeElement.value = '';
        return;
      }

      this.fileForm.patchValue({
        fileName: file.name,
        document: file,
        uploadDate: new Date(Date.now()).toISOString(),
      });
    }
  }

  addFile() {
    if (this.fileForm.valid) {
      const data = this.fileForm.value;
      this.filedataSource.data = [...this.filedataSource.data, data];

      this.fileForm.reset();
      this.fileInput!.nativeElement.value = '';

      this.filesTableHasAtLeastAnItem = true;
      this.checkFormsValidity();

      this.dialog.open(AlertComponent, {
        disableClose: true,
        data: {
          message: 'budget-inclusion.file-register-successfull',
          param: null,
          title: 'alert.information',
        },
      });
    }
  }

  downloadFile(element: any) {
    this.budgetsService.downloadFile(element.id).subscribe({
      next: (response: Blob) => {
        const blob = new Blob([response], { type: 'application/octet-stream' });
        saveAs(blob, element.fileName);
      },
      error: () => {
        console.error('Arquivo não encontrado!');
      },
    });
  }

  deleteFile(element: any) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: { message: 'budget-inclusion.deleteFile' }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result.confirm) {
        const updatedFiles = this.filedataSource.data.filter(file => file !== element);
        this.filedataSource.data = updatedFiles;

        this.dialog.open(AlertComponent, {
          disableClose: true,
          data: {
            message: 'budget-inclusion.file-delete-successfull',
            param: null,
            title: 'alert.information'
          },
        });

        if (updatedFiles.length === 0) {
          this.filesTableHasAtLeastAnItem = false;
          this.checkFormsValidity();
        }
      }
    });
  }

  cancel() {
    if (this.isReadOnly) {
      localStorage.setItem('tab', '1');
      this.router.navigate(['/order-details', this.orderId]);
    } else {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        data: { message: 'budget-inclusion.discardChangesConfirmation' }
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result.confirm) {
          localStorage.setItem('tab', '1');
          this.router.navigate(['/order-details', this.orderId]);
        }
      });
    }
  }

  save() {
    const loader = this.dialog.open(LoaderDialogComponent, {
      disableClose: true,
    });
    const orderItemValues = this.orderItemForms
      .map((form, index) => ({
        orderItemId: this.orderItems[index].orderItemId ?? this.orderItems[index].id,
        availableQuantity: form.get('availableQuantity')?.value,
        unitValue: form.get('unitValue')?.value
      }));

    const additionalCosts = this.dataSource.data
      .map((cost: any) => ({
        id: cost.id,
        costType: {
          id: cost.costType?.id,
          costType: cost.costType?.costType
        },
        description: cost.description,
        value: cost.value
      }));

    const formData = {
      items: orderItemValues,
      additionalCosts: additionalCosts,
      budgetResponseDate: this.additionalInfoForm.value.budgetResponseDate,
      paymentTermDays: this.additionalInfoForm.value.paymentTermDays,
      deliveryTermDays: this.additionalInfoForm.value.deliveryTermDays,
      budgetObservations: this.additionalInfoForm.value.budgetObservations,
      totalValue: this.totalOrderValue
    };

    const data = {
      ...formData,
      filesDTO: this.filedataSource.data,
    };

    this.budgetsService.includeBudget(this.budgetId, data)
      .pipe(finalize(() => loader.close()))
      .subscribe({
        next: () => {
          const dialogRef = this.dialog.open(AlertComponent, {
            disableClose: true,
            data: {
              message: 'budget-inclusion.successfull-save',
              param: null,
              title: 'alert.information',
            },
          });
          dialogRef.afterClosed().subscribe(() => {
            localStorage.setItem('tab', '1');
            this.router.navigate(['/order-details', this.orderId]);
          });
        },
        error: (err) => {
          this.dialog.open(AlertComponent, {
            disableClose: true,
            data: {
              message: 'budget-inclusion.error.saveBudget',
              title: 'alert.information'
            },
          });
        }
      });
  }

  validateAdditionalCosts(): void {
    const costTypeControl = this.additionalCostsForm.get('costType');
    const descriptionControl = this.additionalCostsForm.get('description');
    const valueControl = this.additionalCostsForm.get('value');

    costTypeControl?.clearValidators();
    descriptionControl?.clearValidators();
    valueControl?.clearValidators();

    if (costTypeControl?.value) {
      costTypeControl!.setValidators(Validators.required);
      valueControl!.setValidators(Validators.required);
    }

    if (costTypeControl?.value.costType === 'Outros') {
      descriptionControl?.setValidators(Validators.required);
    }

    costTypeControl?.updateValueAndValidity();
    descriptionControl?.updateValueAndValidity();
    valueControl?.updateValueAndValidity();
  }

  addAdditionalCost() {
    if (this.additionalCostsForm.valid) {
      const additionalCost = this.additionalCostsForm.value;
      this.dialog.open(AlertComponent, {
        disableClose: true,
        data: {
          message: 'budget-inclusion.additional-cost-register-successfull',
          param: null,
          title: 'alert.information',
        },
      });
      this.dataSource.data = [...this.dataSource.data, additionalCost];
      this.additionalCostsForm.reset();
      this.calculateTotalOrderValue();
    }
  }

  editAdditionalCost(element: any) {
    this.isEdit = true;
    this.editingCost = element;
    this.additionalCostsForm.patchValue({
      costType: this.costTypeOptions.find(option => option.id === element.costType.id) || element.costType,
      description: element.description,
      value: element.value
    });
  }

  hasAdditionalCosts(): boolean {
    return this.dataSource.data?.length > 0;
  }

  updateAdditionalCost() {
    if (this.additionalCostsForm.valid && this.editingCost) {
      this.editingCost.costType = this.additionalCostsForm.get('costType')?.value;
      this.editingCost.description = this.additionalCostsForm.get('description')?.value;
      this.editingCost.value = this.additionalCostsForm.get('value')?.value;

      this.dialog.open(AlertComponent, {
        disableClose: true,
        data: {
          message: 'budget-inclusion.additional-cost-update-successfull',
          param: null,
          title: 'alert.information',
        },
      });

      this.dataSource.data = [...this.dataSource.data]

      this.additionalCostsForm.reset();
      this.isEdit = false;
      this.editingCost = null;
      this.calculateTotalOrderValue();
    }
  }

  deleteAdditionalCost(element: any) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: { message: 'budget-inclusion.deleteAdditionalCost' }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result.confirm) {
        this.dataSource.data = this.dataSource.data.filter(cost => cost !== element);
        this.dialog.open(AlertComponent, {
          disableClose: true,
          data: {
            message: 'budget-inclusion.additional-cost-delete-successfull',
            param: null,
            title: 'alert.information',
          },
        });
        this.calculateTotalOrderValue();
      }
    });
  }

  blockInvalidCharacters(event: KeyboardEvent): void {
    if (event.key === '-' || event.key === '+') {
      event.preventDefault();
    }
  }

  allowOnlyDigits(event: KeyboardEvent): void {
    const editKeys = ['Backspace','Delete','ArrowLeft','ArrowRight','Tab'];
    if (editKeys.includes(event.key)) {
      return;
    }
    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
    }
  }

  onAvailableQtyBlur(index: number): void {
    const ctrl = this.orderItemForms[index].get('availableQuantity')!;
    if (ctrl.value === '' || ctrl.value == null) {
      ctrl.setValue(0);
    }
  }

  calculateTotalOrderValue() {
    const itemTotal = this.orderItemForms.reduce((sum, form) => sum + (form.get('totalValue')?.value || 0), 0);
    const additionalCostTotal = this.dataSource.data.reduce((sum, cost) => sum + (cost.value || 0), 0);
    this.totalOrderValue = itemTotal + additionalCostTotal;
  }

  onCurrencyBlur(form: FormGroup, controlName: string): void {
    const ctrl = form.get(controlName)!;
    const raw = ctrl.value;
    if ((raw === null || raw === undefined || raw.toString().trim() === '')) {
      ctrl.setValue(0);
    }
  }

  /*
    Converte o valor formatado para número, removendo os pontos e substituindo a vírgula por ponto.
    e garante valor > 0 para habilitar o botão.
  */
  get valueNumeric(): number {
    const raw = this.additionalCostsForm.get('value')?.value || '';
    const normalized = raw.toString().replace(/\./g, '').replace(',', '.');
    const num = parseFloat(normalized);
    return isNaN(num) ? 0 : num;
  }
}
